// @givmo/compliance — "Upload documents" (CCM-R1-INTAKE (a); frame 05; set C Q8; the custody line
// as corrected and approved on 2026-09-30, 08:05 PDT).
//
// Several documents at once, no sorting and no labels: custody only. Each file goes through the
// vault's three calls (`uploadArtifactFile`, shared with every upload control), one after another,
// and its row says what happened: done, or refused in the vault's own words. A `.docm` whose macros
// were removed says so under its row, once.
//
// ⚠ NOTHING IS READ. The documents are kept as uploaded; nothing on the calendar comes from them.
// The custody line says so where the choice is made.

import { useRef, useState, type DragEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, FOCUS_RING, Icon, Sheet, cn, testIdProp } from '@givmo/design-system';
import { Check, Loader2, UploadCloud } from 'lucide-react';
import {
  ARTIFACT_TYPE_REFUSED_COPY,
  MACROS_REMOVED_COPY,
  uploadArtifactFile,
  uploadRefusalMessage,
} from './artifact-upload';
import { useCompliance } from './compliance-context';
import {
  ARTIFACT_FILE_ACCEPT,
  artifactContentTypeFor,
  displayArtifactSize,
} from './compliance-decode';
const PREFIX = 'compliance-upload';

/**
 * What the vault does with an upload (CCM-R1-INTAKE-WORD). Frame 05's footer, corrected to be true:
 * macros are removed from a macro-enabled Word file (`.docm`) only — a `.doc` is kept as uploaded,
 * macros and all, and never opened (D-CCM-21 Q8). Jonathan approved the corrected sentence word for
 * word on 2026-09-30 (08:05 PDT): "Macro-enabled Word files (.docm) are saved with their macros
 * removed."
 */
export const DOCUMENT_CUSTODY_COPY =
  'We keep the originals as you upload them. Macro-enabled Word files (.docm) are saved with their ' +
  'macros removed. Givmo doesn’t read documents yet, so nothing on your calendar comes from them; ' +
  'reading them into filings and dates comes later.';

interface UploadRow {
  key: string;
  name: string;
  size: number;
  state: 'uploading' | 'done' | 'refused';
  /** The refusal, in the vault's words; or the macros notice for a `.docm` done. */
  note?: string;
}

export interface UploadDocumentsSheetProps {
  open: boolean;
  onClose: () => void;
  /**
   * Each file kept, by its name, as it is kept: the review's question says what became of it
   * (CCM-R1-REVIEW-WEB, N23). The file answers nothing yet (G7).
   */
  onUploaded?: (fileName: string) => void;
}

export function UploadDocumentsSheet({ open, onClose, onUploaded }: UploadDocumentsSheetProps) {
  const { orgRef, operations } = useCompliance();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const sequence = useRef(0);
  const [rows, setRows] = useState<UploadRow[]>([]);
  const [dragging, setDragging] = useState(false);

  const update = (key: string, change: Partial<UploadRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...change } : row)));

  /** Each file in turn: the type checked against the vault's allow-list, then the three calls. */
  const add = (files: readonly File[]) => {
    for (const file of files) {
      sequence.current += 1;
      const key = `${sequence.current}:${file.name}`;
      const contentType = artifactContentTypeFor(file);
      if (contentType === undefined) {
        setRows((current) => [
          ...current,
          {
            key,
            name: file.name,
            size: file.size,
            state: 'refused',
            note: ARTIFACT_TYPE_REFUSED_COPY,
          },
        ]);
        continue;
      }
      setRows((current) => [
        ...current,
        { key, name: file.name, size: file.size, state: 'uploading' },
      ]);
      queue.current = queue.current.then(async () => {
        try {
          const artifact = await uploadArtifactFile(operations, orgRef, file, contentType);
          update(key, {
            state: 'done',
            size: artifact.byteSize ?? file.size,
            ...(artifact.macrosRemoved ? { note: MACROS_REMOVED_COPY } : {}),
          });
          onUploaded?.(file.name);
          void queryClient.invalidateQueries({ queryKey: ['compliance', 'artifacts', orgRef] });
        } catch (error) {
          update(key, { state: 'refused', note: uploadRefusalMessage(error) });
        }
      });
    }
  };

  const close = () => {
    setRows([]);
    setDragging(false);
    onClose();
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    add([...event.dataTransfer.files]);
  };

  return (
    <Sheet.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
    >
      {open && (
        <Sheet.Content size="lg" testId={PREFIX} initialFocus={false}>
          <Sheet.Header
            title="Upload documents"
            description="Anything you keep for your filings. No sorting or labels needed."
          />
          <Sheet.Body className="flex flex-col gap-20">
            <div
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={cn(
                'flex flex-col items-center gap-8 rounded-xl border border-dashed border-border-strong bg-surface-sunken px-24 py-32 text-center',
                dragging && 'border-brand-500',
              )}
              {...testIdProp(`${PREFIX}-drop`)}
            >
              <Icon icon={UploadCloud} size={22} className="text-icon-muted" aria-hidden />
              {/* A phone cannot drop a file, so below `sm` the box says only "Choose files"
                  (Jonathan's ruling, 2026-09-30 22:46 PDT). */}
              <p className="text-s14 font-w500 text-text">
                <span className="max-sm:hidden" {...testIdProp(`${PREFIX}-drop-words`)}>
                  Drop files here, or{' '}
                </span>
                <button
                  type="button"
                  onClick={() => inputRef.current?.click()}
                  className={cn(
                    'rounded-xs text-brand-600 underline underline-offset-2',
                    FOCUS_RING,
                  )}
                  {...testIdProp(`${PREFIX}-choose`)}
                >
                  <span className="max-sm:hidden">choose files</span>
                  <span className="sm:hidden" {...testIdProp(`${PREFIX}-choose-phone`)}>
                    Choose files
                  </span>
                </button>
              </p>
              {/* Set C Q8: Word (.docx, .doc, .docm) joins the vault's types. */}
              <p className="text-s12 text-text-tertiary">PDF, Word, JPEG, PNG, HEIC or TIFF</p>
              <input
                ref={inputRef}
                type="file"
                multiple
                accept={ARTIFACT_FILE_ACCEPT}
                className="hidden"
                onChange={(event) => {
                  const files = [...(event.target.files ?? [])];
                  // Allow picking the same file again after a refusal.
                  event.target.value = '';
                  add(files);
                }}
                {...testIdProp(`${PREFIX}-input`)}
              />
            </div>
            {rows.length > 0 && (
              <ul className="flex flex-col" {...testIdProp(`${PREFIX}-list`)}>
                {rows.map((row) => (
                  <li
                    key={row.key}
                    className="flex flex-col gap-2 border-b border-border-subtle py-12 last:border-b-0"
                    {...testIdProp(`${PREFIX}-row`)}
                  >
                    <div className="flex items-center gap-10">
                      <span className="min-w-0 flex-1 truncate text-s13 text-text">{row.name}</span>
                      <span className="shrink-0 text-s12 text-text-tertiary">
                        {displayArtifactSize(row.size)}
                      </span>
                      {row.state === 'done' && (
                        <Icon
                          icon={Check}
                          size={16}
                          className="shrink-0 text-success-600"
                          aria-label="Uploaded"
                        />
                      )}
                      {row.state === 'uploading' && (
                        <Icon
                          icon={Loader2}
                          size={16}
                          className="shrink-0 animate-spin text-icon-muted motion-reduce:animate-none"
                          aria-label="Uploading"
                        />
                      )}
                    </div>
                    {row.note !== undefined && (
                      <p
                        className={cn(
                          'text-s12',
                          row.state === 'refused' ? 'text-neutral-700' : 'text-text-secondary',
                        )}
                        {...testIdProp(`${PREFIX}-note`)}
                      >
                        {row.note}
                      </p>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <p
              className="text-s13 leading-h15 text-text-secondary"
              {...testIdProp(`${PREFIX}-custody`)}
            >
              {DOCUMENT_CUSTODY_COPY}
            </p>
          </Sheet.Body>
          <Sheet.Footer>
            <Button appearance="saas" tone="primary" onClick={close} testId={`${PREFIX}-done`}>
              Done
            </Button>
          </Sheet.Footer>
        </Sheet.Content>
      )}
    </Sheet.Root>
  );
}
